import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { pathOr } from 'ramda'
import { RootState } from 'store'

import PackagesRepository from 'services/repositories/PackagesRepository'
import { IPagination } from 'types/assets'
import { IPackage, IPackagesQueryPayload } from 'types/packages'
import { IPackagesState } from 'types/slices'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import { handleApiErrors } from 'utils/errors'

const initialState: IPackagesState = {
  status: 'initial',
  packages: [],
  singlePackage: null,
  pagination: {
    page: 0,
    pagesTotal: 0,
    recordsTotal: 0,
    take: 0
  }
}

export const types = {
  getAllPackages: 'packages/getAllPackages',
  getPackageByPublicToken: 'packages/getPackageByPublicToken',
  deletePackages: 'packages/deletePackages',
  getPackagesByType: 'packages/getPackagesByType'
} as const

export const getAllPackages = createAsyncThunk(
  types.getAllPackages,
  async (query: IPackagesQueryPayload, { rejectWithValue }) => {
    try {
      const response = await PackagesRepository.getAllPackages({ query })

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getPackageByPublicToken = createAsyncThunk(
  types.getPackageByPublicToken,
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await PackagesRepository.getPackageByPublicToken(token)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const deletePackages = createAsyncThunk(
  types.deletePackages,
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await PackagesRepository.deletePackages(ids)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const packagesSlice = createSlice({
  name: 'packages',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllPackages.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getAllPackages.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.packages = action.payload.data
        state.pagination = action.payload.meta
      })
      .addCase(getAllPackages.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getPackageByPublicToken.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getPackageByPublicToken.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.singlePackage = action.payload
      })
      .addCase(getPackageByPublicToken.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(deletePackages.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(deletePackages.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(deletePackages.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
  }
})

export const selectPackages = (state: RootState) => state.packages
export const selectStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectPackages, packagesSlice => packagesSlice.status)

export const selectPackagesPagination: (state: RootState) => IPagination =
  createSelector(
    selectPackages,
    pathOr(
      {
        page: 0,
        pagesTotal: 0,
        recordsTotal: 0,
        take: 0
      },
      ['pagination']
    )
  )

export const selectAllPackages: (state: RootState) => IPackage[] =
  createSelector(selectPackages, pathOr([], ['packages']))

export const selectPackageByPublicToken: (state: RootState) => IPackage | null =
  createSelector(selectPackages, packagesSlice => packagesSlice.singlePackage)

export default packagesSlice.reducer
